/**
 * Square 進入頁（依後綴 /lite/shop/:suffix）
 * 未登入時顯示 LINE 登入按鈕；優先使用 LIFF，非 LIFF 時 fallback 後端 LINE Login。
 * 登入後顯示該 square 活動列表與商家資訊（訂閱方案、發佈次數／上限、試用期，與 Group 一致）
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getApiBase } from '../../api/apiUtils';
import {
  getSquareBySuffix,
  switchSquareForJwt,
} from '../../api/liteSquaresClient';
import {
  getPrepaidList,
  registerNewUser,
  type PrepaidListItem,
} from '../../api/contextClient';
import { setLiteJwtToken } from '../../api/liteAuthToken';
import {
  getSubscriptionStatus,
  type SubscriptionStatus,
} from '../../api/liteSubscriptionClient';
import { utcToTaipei, DAY_MS } from '@pos/shared/utils/time';
import { isLitePrepaidShopType } from '@pos/shared';
import { isLitePaidPlan } from '../../utils/liteSubscription';
import { getLiteJwtTokenFromUrlOrStorage } from '../../utils/liteJwtToken';
import {
  litePathShopSubscription,
  litePathShopSubscriptionCancel,
  litePathShopSetting,
  litePathShopCreateActivity,
  litePathShopDeliver,
  litePathShopDeliverResult,
} from '../../utils/liteRoutes';
import { Button } from '@/components/ui/button';
import { StoreDashboardView } from '@/components/StoreDashboardView';
import { ActivityDeliveryBlock } from '@/components/ActivityDeliveryBlock';
import { MerchantSubscriptionBlock } from '@/components/MerchantSubscriptionBlock';
import { PrepaidListBlock } from '@/components/PrepaidListBlock';
import { LITE_SOCIAL_LINKS } from '@/constants/social';
import { buildLiteJoinUrl } from '../../utils/lite-join-url';
import { useLiff } from '../../contexts/LiffContext';

/** Base64URL 編碼（支援 UTF-8，避免 btoa 對中文等非 Latin1 字元報錯） */
function base64UrlEncodeUtf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64UrlEncodeState(state: {
  redirect_uri: string;
  mode: string;
  suffix?: string;
}): string {
  const json = JSON.stringify(state);
  return base64UrlEncodeUtf8(json);
}

export function SquareLandingPage() {
  const { suffix } = useParams<{ suffix: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { liff, isReady, isLoggedIn, shouldUseLiff } = useLiff();
  const [liffLoginError, setLiffLoginError] = useState<string | null>(null);
  const [liffLoginInProgress, setLiffLoginInProgress] = useState(false);
  const hasTriedLiffLoginRef = useRef(false);

  const [square, setSquare] = useState<{
    id: string;
    name: string;
    type?: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** 訂閱狀態：與 Group 一致（目前方案、已使用／上限、試用期） */
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [subStatusLoading, setSubStatusLoading] = useState(false);

  /** 預繳名單（名稱、次數、性別、最近更新） */
  const [prepaidList, setPrepaidList] = useState<PrepaidListItem[]>([]);
  const [prepaidListLoading, setPrepaidListLoading] = useState(false);

  /** JWT：優先 URL 帶入並寫入 storage，其次從 storage 取得 */
  const token =
    typeof window !== 'undefined' ? getLiteJwtTokenFromUrlOrStorage() : null;

  // 從 URL 取得 token 並儲存（OAuth callback 導回時）
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl && suffix) {
      try {
        setLiteJwtToken(tokenFromUrl);
      } catch {
        // localStorage 不可用時略過（如私密模式）
      }
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      next.delete('suffix');
      const q = next.toString();
      navigate(`/lite/shop/${suffix}${q ? `?${q}` : ''}`, { replace: true });
    }
  }, [searchParams, suffix, navigate]);

  // 載入 square 資訊（公開）；suffix 支援 6–8 碼（與後端一致）
  useEffect(() => {
    if (!suffix || suffix.length < 6 || suffix.length > 8) {
      setError('連結格式錯誤');
      setLoading(false);
      return;
    }

    let cancelled = false;
    getSquareBySuffix(suffix)
      .then(info => {
        if (cancelled) return;
        if (!info) {
          setError('Square 不存在或已停用');
          setLoading(false);
          return;
        }
        setSquare({
          id: info.id,
          name: info.name,
          type: info.type ?? null,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('無法載入 Square 資訊');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [suffix]);

  // 訂閱狀態：與 Group 一致，依 squareSuffix 取得（後端依該 Square 所屬使用者解析群組訂閱）
  const fetchSubStatus = useCallback(() => {
    if (!suffix || suffix.length < 6 || suffix.length > 8 || !token) return;
    setSubStatusLoading(true);
    getSubscriptionStatus(token, undefined, { squareSuffix: suffix })
      .then(setSubStatus)
      .catch(() => setSubStatus(null))
      .finally(() => setSubStatusLoading(false));
  }, [suffix, token]);

  useEffect(() => {
    if (suffix && suffix.length >= 6 && suffix.length <= 8 && token) {
      fetchSubStatus();
    } else {
      setSubStatus(null);
      setSubStatusLoading(false);
    }
  }, [suffix, token, fetchSubStatus]);

  /** 預繳名單：依 suffix 與 JWT 載入 */
  useEffect(() => {
    if (!suffix) {
      setPrepaidList([]);
      return;
    }
    const jwt = getLiteJwtTokenFromUrlOrStorage();
    if (!jwt) {
      setPrepaidList([]);
      return;
    }
    let cancelled = false;
    setPrepaidListLoading(true);
    getPrepaidList(jwt, { squareSuffix: suffix })
      .then(list => {
        if (!cancelled) setPrepaidList(list);
      })
      .catch(() => {
        if (!cancelled) setPrepaidList([]);
      })
      .finally(() => {
        if (!cancelled) setPrepaidListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [suffix]);

  const trialDaysLeft = useMemo(() => {
    if (
      !subStatus?.isTrial ||
      !subStatus.trialEndsAt ||
      isLitePaidPlan(subStatus.planSlug)
    ) {
      return null;
    }
    const nowTaipei = new Date(utcToTaipei(new Date().toISOString()));
    const trialEndTaipei = new Date(utcToTaipei(subStatus.trialEndsAt));
    const diffMs = trialEndTaipei.getTime() - nowTaipei.getTime();
    return Math.max(0, Math.ceil(diffMs / DAY_MS));
  }, [subStatus]);

  /** LIFF 已登入但尚無 JWT 時：getIDToken → register → switchSquareForJwt → 存 JWT，觸發重新渲染 */
  useEffect(() => {
    const currentToken = getLiteJwtTokenFromUrlOrStorage();
    if (
      currentToken ||
      !suffix ||
      suffix.length < 6 ||
      suffix.length > 8 ||
      !shouldUseLiff ||
      !isReady ||
      !liff ||
      !isLoggedIn ||
      hasTriedLiffLoginRef.current
    ) {
      return;
    }
    hasTriedLiffLoginRef.current = true;
    setLiffLoginInProgress(true);
    setLiffLoginError(null);
    const run = async () => {
      try {
        const lineIdToken = await Promise.resolve(liff.getIDToken());
        if (!lineIdToken) throw new Error('無法取得 LINE 認證');
        const result = await registerNewUser({ lineIdToken });
        if (!result.jwtToken) throw new Error('無法取得登入憑證');
        const newToken = await switchSquareForJwt(result.jwtToken, suffix);
        setLiteJwtToken(newToken);
      } catch (err) {
        setLiffLoginError(
          err instanceof Error ? err.message : '登入失敗，請稍後再試'
        );
        hasTriedLiffLoginRef.current = false;
      } finally {
        setLiffLoginInProgress(false);
      }
    };
    run();
  }, [suffix, shouldUseLiff, isReady, liff, isLoggedIn]);

  /** 使用 LINE 登入：LIFF 時呼叫 liff.login，否則 fallback 後端 LINE Login */
  const handleLineLogin = () => {
    setLiffLoginError(null);
    if (shouldUseLiff && liff) {
      const redirectUri = `${window.location.origin}/lite/shop/${suffix}`;
      liff.login({ redirectUri });
      return;
    }
    const apiBase = getApiBase();
    if (!apiBase) {
      setError('API 位址未設定');
      return;
    }
    const redirectUri = `${window.location.origin}/lite/shop/${suffix}`;
    const state = base64UrlEncodeState({
      redirect_uri: redirectUri,
      mode: 'enter',
      suffix: suffix ?? '',
    });
    const url = `${apiBase}/api/lite/auth/line?redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <p className="text-slate-600">載入中...</p>
      </div>
    );
  }

  if (error || !square) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100">
        <p className="text-red-600 mb-4">{error || 'Square 不存在'}</p>
        <a href="/lite/new-square" className="text-indigo-600 underline">
          建立 Square
        </a>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white shadow-lg p-6">
          <h1 className="text-xl font-semibold text-slate-800 text-center mb-1">
            {square.name}
          </h1>
          <p className="text-sm text-slate-500 text-center mb-6">
            使用 LINE 登入以進入此場域
          </p>
          {liffLoginInProgress ? (
            <p className="text-slate-600 text-center py-3">登入中…</p>
          ) : (
            <button
              type="button"
              onClick={handleLineLogin}
              className="w-full rounded-lg bg-[#06C755] text-white font-medium py-3 hover:opacity-90 transition"
            >
              使用 LINE 登入進入
            </button>
          )}
          {liffLoginError && (
            <p className="mt-3 text-sm text-red-600 text-center" role="alert">
              {liffLoginError}
            </p>
          )}
        </div>
      </div>
    );
  }

  // 已登入且已有 square：與 Group 共用活動交付區塊（可收合、統計、結算、分享）
  const activityDeliveryContent = suffix ? (
    <ActivityDeliveryBlock
      mode="square"
      squareSuffix={suffix}
      onCreateActivity={() => navigate(litePathShopCreateActivity(suffix))}
      onNavigateToDeliver={id => navigate(litePathShopDeliver(suffix, id))}
      onNavigateToResult={id => navigate(litePathShopDeliverResult(suffix, id))}
      getActivityJoinUrl={id =>
        buildLiteJoinUrl({
          origin: typeof window !== 'undefined' ? window.location.origin : '',
          suffix,
          activityId: id,
        })
      }
    />
  ) : null;

  // 與 Group 一致：商家資訊卡右上角「訂閱」「前往設定」
  const merchantExtraButtons = suffix ? (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(litePathShopSubscription(suffix))}
      >
        訂閱方案
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(litePathShopSetting(suffix))}
      >
        前往設定
      </Button>
    </>
  ) : null;

  // 與 Group 一致：使用 SSOT 元件顯示訂閱方案、發佈次數／上限、試用期
  const merchantSubscriptionBlock =
    subStatusLoading || subStatus ? (
      <MerchantSubscriptionBlock
        loading={subStatusLoading}
        subStatus={subStatus}
        onGoToSubscription={() =>
          suffix && navigate(litePathShopSubscription(suffix))
        }
        onOpenCancel={() =>
          suffix && navigate(litePathShopSubscriptionCancel(suffix))
        }
      />
    ) : null;

  const merchantPrepaidBlock =
    isLitePrepaidShopType(square?.type) && suffix ? (
      <PrepaidListBlock
        loading={prepaidListLoading}
        items={prepaidList}
        emptyMessage="尚無預繳名單"
      />
    ) : undefined;

  return (
    <StoreDashboardView
      storeName={square.name}
      storeType={square.type ?? null}
      socialLinks={LITE_SOCIAL_LINKS}
      activityDeliveryContent={activityDeliveryContent}
      merchantExtraButtons={merchantExtraButtons}
      merchantSubscriptionBlock={merchantSubscriptionBlock}
      merchantPrepaidBlock={merchantPrepaidBlock}
      onCreateActivity={() =>
        suffix && navigate(litePathShopCreateActivity(suffix))
      }
    />
  );
}
